'use client';

import { ArrowRight, Download, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useLocale } from '@/components/common/locale-provider';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/hooks/use-translations';
import { capture } from '@/lib/analytics';

interface PluginDownloadStepProps {
  onContinue: () => Promise<void> | void;
}

export function PluginDownloadStep({ onContinue }: PluginDownloadStepProps) {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const [isContinuing, setIsContinuing] = useState(false);

  useEffect(() => {
    capture('plugin_downloads_step_shown', {});
  }, []);

  const handleContinue = () => {
    if (isContinuing) return;

    setIsContinuing(true);
    capture('plugin_downloads_step_skipped', {});
    const next = onContinue();
    if (next && typeof next.then === 'function') {
      next
        .catch(() => {
          // Keep the spinner visible on success paths (redirect/hand-off),
          // but recover if the callback rejects.
        })
        .finally(() => {
          setIsContinuing(false);
        });
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-5">
      <div className="text-center">
        <h2 className="text-xl font-semibold">
          {t('downloads.verificationStep.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('downloads.verificationStep.subtitle')}
        </p>
      </div>

      <div className="flex flex-col w-full gap-2">
        <Button className="w-full" asChild>
          <Link
            href="/downloads"
            onClick={() => {
              capture('plugin_downloads_page_opened', {
                source: 'verification_step',
              });
            }}
          >
            <Download className="size-4" />
            {t('downloads.verificationStep.viewDownloads')}
          </Link>
        </Button>

        <Button
          variant="outline"
          onClick={() => {
            void handleContinue();
          }}
          disabled={isContinuing}
          className="w-full gap-1"
        >
          {isContinuing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ArrowRight className="size-4" />
          )}
          {t('downloads.verificationStep.continueToApp')}
        </Button>
      </div>
    </div>
  );
}
